/**
 * Werkstatt-Zweige.
 *
 * Die Pipeline-Werkstatt hat mehrere thematisch eigenständige Zweige, jeweils
 * mit ihren eigenen Phasen/Sections. Jeder Zweig folgt dem Bienenwabe-Pattern:
 * ctx-bound Inspector + Step-Liste mit UUID-Identifiern.
 *
 *  - "abrechnung" — der ursprüngliche Pipeline-Zweig: PDF → OCR → Validierung →
 *                   Berechnung → Review → Export → (Konflikt-Branch).
 *
 *  - "zugriff"   — Glass-Box-Aufsicht über Rollen × Berechtigungen × Code-
 *                   Pointer. Aufgesetzt 2026-05-19, ausgelöst durch Drift-
 *                   Bug bei Migration 033 (Role "developer"): das gleiche Gate
 *                   war in 5 Files mit 4 verschiedenen Formulierungen
 *                   implementiert. Single-Source via lib/permissions.ts.
 *
 *  - "bestand"   — Karten-Aggregat pro Monat × Mandant. Aufgesetzt 2026-05-19,
 *                   ausgelöst durch Counter-Drift im Kanban: Tab-Badges zeigten
 *                   0 für nicht-aktive Kategorien, weil die client-side
 *                   useMemo aus dem bereits server-gefilterten cards[]-Array
 *                   zählte. Single-Source via GET /api/cards/counts +
 *                   Drift-Check der Aggregat-vs-Liste-Konsistenz.
 */

export type WerkstattBranch = "abrechnung" | "zugriff" | "bestand" | "performance" | "grid-audit";

export const WERKSTATT_BRANCHES: ReadonlyArray<{
  slug: WerkstattBranch;
  title: string;
  subtitle: string;
  defaultSection: string;
}> = [
  {
    slug: "abrechnung",
    title: "Abrechnungs-Pipeline",
    subtitle: "PDF → OCR → Validierung → Berechnung → RZL-Export",
    defaultSection: "upload",
  },
  {
    slug: "bestand",
    title: "Bestand",
    subtitle: "Karten-Aggregat des Monats — kanonische Counter-Quelle",
    defaultSection: "overview",
  },
  {
    slug: "zugriff",
    title: "Zugriffs-Matrix",
    subtitle: "Rollen × Berechtigungen × Code-Pointer — Drift-Detektor",
    defaultSection: "matrix",
  },
  {
    slug: "performance",
    title: "Performance",
    subtitle: "Karten-Öffnen-Latenz + Connection-Pool-Drift — Resource-Leak-Test",
    defaultSection: "karten-open",
  },
  {
    slug: "grid-audit",
    title: "Grid-Audit",
    subtitle: "Highlight-Qualität pro Zelle — richtige Zelle? vollständig? (Baseline-Messung vor/nach Grid-Änderungen)",
    defaultSection: "card",
  },
];

/** Phasen-Slugs des Abrechnungs-Zweigs. Wird für Backwards-Compat-Redirects benötigt. */
export const ABRECHNUNG_PHASE_SLUGS = [
  "upload",
  "ocr",
  "validierung",
  "berechnung",
  "review",
  "export",
  "konflikt",
] as const;

export type AbrechnungPhase = (typeof ABRECHNUNG_PHASE_SLUGS)[number];

/** Sections des Zugriffs-Zweigs. */
export const ZUGRIFF_SECTION_SLUGS = [
  "matrix",
  "hierarchy",
  "server-probe",
  "client-scan",
  "drift",
] as const;

export type ZugriffSection = (typeof ZUGRIFF_SECTION_SLUGS)[number];

export const ZUGRIFF_SECTIONS: ReadonlyArray<{
  slug: ZugriffSection;
  num: number;
  title: string;
}> = [
  { slug: "matrix", num: 1, title: "Permission-Registry" },
  { slug: "hierarchy", num: 2, title: "Rollen-Hierarchie" },
  { slug: "server-probe", num: 3, title: "Server-Gates (Live-Probe)" },
  { slug: "client-scan", num: 4, title: "Client-Gates (DOM-Scan)" },
  { slug: "drift", num: 5, title: "Drift-Detektor" },
];

/** Sections des Bestand-Zweigs. Nur eine Section („overview") — der Zweig
 *  hat zur Zeit keine Sub-Navigation, weil alle Steps gemeinsam auf einer
 *  Seite Sinn ergeben (Aggregat + Drift-Check + kanonische-Quelle-Notice). */
export const BESTAND_SECTION_SLUGS = ["overview"] as const;
export type BestandSection = (typeof BESTAND_SECTION_SLUGS)[number];

/** Sections des Performance-Zweigs. */
export const PERFORMANCE_SECTION_SLUGS = ["karten-open"] as const;
export type PerformanceSection = (typeof PERFORMANCE_SECTION_SLUGS)[number];
